/**
 * V1.0
 *
 * User: tomers
 * Date: 6/25/13
 *
 * This component is used to show a gallery of items.
 * Only one item can be selected.
 * In order to listen fro outside the component to the chosen item you should listen on the selectable collection "change:selection".
 * When the "new item" is selected, the selectedId on the collection will be -1 (because no item in the collection is really selected) but on the event includes options object with "isNewItemSelected" property
 *
 * Options:
 * el - mandatory. jQuery element. The element on the page where the component will be created.
 * collection - mandatory. Selectable Collection. The collection that being shown in the gallery
 * permutation - optional. One of the options in the static Gallery.PERMUTATION enum. no permutation is the default
 * newItemToAdd - optional. Object. {name: XXXXXXX}. If there is newItemToAdd we are adding a button with the name in the object at the end of all other buttons.
 * ellipsis - optional. numeric value that tells how many lines to show and trim the rest with ellipsis for gallery item name . default is 2.
 * descriptionEllipsis - optional. same for gallery item description. default is 3.
 *
 * Using Example:
 *---------------------------
 * template:
 * this.gallery = new Gallery({
 *      collection : galleryItems,
 *      el : this.ui.galleryList,
 *      permutation : Gallery.PERMUTATION.A
 *      newItemToAdd: { name: "Create new goal" }
 * });
 *
 * this.gallery.render();
 *
 * *******************************************************************************************************
 */


define(function (require) {
    "use strict";
    var $ = require('jquery');
    var _ = require('underscore');
    var Marionette = require('marionette');
    var Logger = require('logger');
    var GalleryCollection = require('ui/gallery/js/collections/GalleryCollection');
    var template = require('ui/gallery/templates/gallery.tmpl');
    var GalleryItemView = require("ui/gallery/js/views/GalleryItemView");
    var GalleryItemModel = require("ui/gallery/js/models/GalleryItemModel");
    var GalleryModel = require("ui/gallery/js/models/GalleryModel");
    var Ellipsis = require("ui.plugins/ellipsis");

    var NEW_ITEM_KEY = "newItemId";
    var NO_SELECTABLE_GALLERY_CLASS = "no-selectable";

    var Gallery = Marionette.CompositeView.extend({

        template:template,
        itemView:GalleryItemView,
        itemViewContainer:".gallery",

        modelEvents:{
            "change:selectedOption":"_onSelectedOptionChanged"
        },
        itemEventsDeprecated:{
            "itemview:itemClick":"_handleItemClick",
            "itemview:editClick":"_handleItemEdit",
            "itemview:viewClick":"_handleItemView"
        },
        ui:{
            itemViewContainer:".gallery"
        },

        //----------------------------------------
        // initialize
        //---------------------------------------

        initialize:function (options) {
            if (!options || !_.isPlainObject(options)) {
                throw "Gallery Component: invalid argument: options is illegal.";
            }

            this.logger = Logger.getLogger('GalleryWidget');
            this.model = new GalleryModel();

            this.options = _.defaults(options, {
                container: null,
                permutation: Gallery.PERMUTATION.NONE,
                newItemToAdd: null,
                collectionMap: {},
                selectedOption: this.model.get("selectedOption"),
                ellipsis: 2,
                descriptionEllipsis: 3,
                selectableGallery: true
            });
            this.options.selectedOption = this.options.selectableGallery ? this.options.selectedOption : null; //if the gallery is not selectable - we override the selected option
            this.collection = _.has(options, "collection") ? this._getReadyCollection(options.collection) : new GalleryCollection();
            this.model.set("selectedOption", this.options.selectedOption);
            this.model.set("selectableGallery", this.options.selectableGallery);

            // Listen to the hover and click events from each option in the list.
            Marionette.bindEntityEvents(this, this, this.itemEventsDeprecated);

            this.itemViewOptions = {
                galleryPermutations: Gallery.PERMUTATION,
                selectedPermutation: this.options.permutation,
                originalCollection: options.collection, //there are cases (AppKeysGalleryCollection for ex.) where the collection is not an instance of GalleryCollection. We pass it to the item view
                selectableGallery: this.options.selectableGallery
            };
        },

        onRender:function () {
            if (!_.isNull(this.options.container)) {
                this.options.container.append(this.$el);
            }
            this.ui.itemViewContainer.addClass(this.options.permutation);
            this.ui.itemViewContainer.toggleClass(NO_SELECTABLE_GALLERY_CLASS, !this.model.isSelectableGallery());
            this._showSelectedOptionInGallery();
        },

        updateDependencies: function(dependenciesArr){
            this.children.each(function(view){
                var dependencyObj = _.find(dependenciesArr, function(dep){
                    return dep.itemId === String(view.model.get("key"));
                });

                if(dependencyObj && !_.isEmpty(dependencyObj.config)){
                    view.updateDependencies(dependencyObj.config);
                }
            });
        },

        scrollToSelectedItem: function () {
            _.defer(_.bind(function() {
                if (this.model.hasSelectedOption()) {
                    var selectedItemView = this._getItemViewByKey(this.model.get('selectedOption'));
                    if (selectedItemView) {
                        this.ui.itemViewContainer.scrollParent().scrollTop(selectedItemView.$el.children()[0].offsetTop);
                    }
                }
            }, this));
        },

        /**
         * Get the items ready for use.
         * If its not instance of the Gallery Items Collection we should convert it with the collection map the user should provide
         *
         * @param collection - the items that sent by the user in the "options" object
         * @return {*} - the items, ready for use by mustache and model
         */
        _getReadyCollection:function (collection) {
            var readyItemsArr = [];

            if (collection instanceof GalleryCollection) {
                readyItemsArr = readyItemsArr.concat(collection.models);
            }
            else {
                var mappedCollection = convertToGalleryCollection(collection, this.options);
                readyItemsArr = readyItemsArr.concat(mappedCollection.models);
            }

            if (!_.isEmpty(this.options.newItemToAdd)) {
                this.options.newItemToAdd.key = _.has(this.options.newItemToAdd, "key") ? this.options.newItemToAdd.key : NEW_ITEM_KEY;
                this.options.newItemToAdd.type = GalleryItemModel.TYPES.NEW;
                readyItemsArr.push(this.options.newItemToAdd);
            }

            var readyCollection = new GalleryCollection(readyItemsArr);

            this._bindEventsToOriginalCollection(collection);

            return readyCollection;
        },

        /**
         * We need to bind each model separately because when we update a model, an "add" trigger is not fired on the collection and only on the model
         * We are listening to the change event on the relevant specific attributes according to the collectionMap
         * @param collection
         * @private
         */
        _bindEventsToOriginalCollection: function(collection) {
            this.listenTo(collection, "add", this._addModelToCollection);
            this.listenTo(collection, "remove", this._removeModelFromCollection);

            _.each(collection.models, _.bind(function(model) {
                this._bindEventsToModel(model);
            }, this));
        },
        _bindEventsToModel: function(model) {
            var nameAttrName = getAttributeName("name", this.options.collectionMap);
            var DescriptionAttrName = getAttributeName("description", this.options.collectionMap);

            this.listenTo(model, "change:"+nameAttrName, this._updateModelInCollection);
            this.listenTo(model, "change:"+DescriptionAttrName, this._updateModelInCollection);
        },

        _addModelToCollection: function(model) {
            this._bindEventsToModel(model);
            var convertedModel = convertToGalleryItemModel(model, this.options);
            var indexToAdd = (_.isNull(this.options.newItemToAdd)) ? this.collection.size() : this.collection.size() - 1; // if there is "new X" button - add the new item before
            this.collection.add(convertedModel.toJSON(), {merge:true, at:indexToAdd});

            // New item has been added - it gets only a render event before it's shown - need to call handle ellipsis explicitly
            _.defer(_.bind(function(){
                Ellipsis.handleNow(this);
            }, this));
        },

        /**
         * If existing model has changed - update its attributes on the gallery
         * @param model
         * @private
         */
        _updateModelInCollection: function(model) {
            var convertedModel = convertToGalleryItemModel(model, this.options);
            var existingModel = this._getItemModelByKey(convertedModel.get("key"));

            if (!_.isEmpty(existingModel)) {
                existingModel.set("name", convertedModel.get("name"));
                existingModel.set("description", convertedModel.get("description"));
            }
        },

        _removeModelFromCollection: function(model) {
            var deletedModelKey = getAttributeFromModel("key", model, this.options.collectionMap);
            var deletedModelFromCollection = this._getItemModelByKey(deletedModelKey);
            this.collection.remove(deletedModelFromCollection);
        },

        /**
         * handles a mouse click on an item. this method selects the clicked item
         * This is triggered by any itemView.
         *
         * @param clickedItemView used for getting reference to the clicked itemView.
         */
        _handleItemClick: function(clickedItemView) {
            var clickedItemKey = clickedItemView.model.get("key");

            this.trigger(Gallery.TRIGGERING.CLICK, clickedItemKey);

            if (this.model.isSelectableGallery()) {
                this._selectItem(clickedItemKey);
            }
        },

        getSelectedItem: function() {
            return this.model.get("selectedOption");
        },

        /**
         * set gallery selection - should be used only from outside
         * @param key
         * @param options  allow selecting in silent mode using {silent: true }  default is silent
         */
        selectItem:function (key, options) {
            options = options || {};
            options.silent = _.has(options, "silent") ? options.silent : true;

            if (this.model.isSelectableGallery()) {
                this._selectItem(key, options);
            }
        },

        /**
         * set the gallery selection - publish an event if silent (via options) is not in use
         * @param key
         * @param options - allow selecting in silent mode using {silent: true }
         * @private
         */
        _selectItem:function (key, options) {
            options = options || {};

            // check that item with this key is exist
            if (_.isEmpty(this._getItemModelByKey(key))) {
                if (_.isNull(key)) {
                    this.model.set("selectedOption", key);
                }
                return;
            }

            this.model.set("selectedOption", key);

            if (!options.silent) {
                this.trigger(Gallery.TRIGGERING.CHANGE, key);
            }
        },

        _onSelectedOptionChanged: function() {
            this._showSelectedOptionInGallery();
        },

        _showSelectedOptionInGallery: function() {
            var oldSelectedOption = this.model.previous("selectedOption");
            var oldSelectedItemView = this._getItemViewByKey(oldSelectedOption);
            if (!_.isEmpty(oldSelectedItemView)) {
                oldSelectedItemView.setSelected(false);
            }

            if (this.model.hasSelectedOption()) {
                var newSelectedOption = this.model.get("selectedOption");
                var selectedItemModel = this._getItemModelByKey(newSelectedOption);
                if (!_.isEmpty(selectedItemModel) && selectedItemModel.isSelectable()){
                    var newSelectedItemView = this._getItemViewByKey(newSelectedOption);
                    newSelectedItemView.setSelected(true);
                }
            }
        },

        /**
         * handles a mouse click on an item edit button. this method edit clicked item
         * This is triggered by any itemView (except new).
         *
         * @param clickedItemView used for getting reference to the clicked itemView.
         */
        _handleItemEdit: function(clickedItemView) {
            var key = clickedItemView.model.get("key");
            this.trigger(Gallery.TRIGGERING.EDIT, key);
        },
        _handleItemView: function(clickedItemView) {
            var key = clickedItemView.model.get("key");
            this.trigger(Gallery.TRIGGERING.VIEW, key);
        },
        _handleDependencyItemClicked: function(clickedItemView, url) {
            this.trigger(Gallery.TRIGGERING.DEPENDENCY_CLICK, url);
        },

        _getItemModelByKey:function (itemKey) {
            var itemModel = this.collection.findWhere({"key":itemKey});
            return itemModel;
        },

        _getItemViewByKey:function (itemKey) {
            var itemView = null;
            var itemModel = this._getItemModelByKey(itemKey);
            if (!_.isEmpty(itemModel)) {
                itemView = this.children.findByModel(itemModel);
            }
            return itemView;
        }

        /*getValue:function () {
         todo: Gal, Itai and Tomer to discuss what is the value of the gallery
         }*/
    });

    /**
     * Converts the items collection to the GalleryCollection, using the collectionMap.
     * For "key" and "value" - If there are no keys in the map, we are taking the "key" and "value" attributes from the item itself
     * For any other optional property we are checking if there is such property in the map or on the item itself and take it only if exsits
     *
     * @param collection - any Backbone Collection to convert to Gallery Collection
     * @param options
     */
    function convertToGalleryCollection(collection, options) {
        var galleryCollection = new GalleryCollection();

        _.each(collection.models, function (model) {
            var galleryItemModel = convertToGalleryItemModel(model, options);
            galleryCollection.add(galleryItemModel);
        });

        return galleryCollection;
    }


    /**
     * Converts a model to a galleryItemModel, using the map
     * For "key" and "name" - If there are no keys in the map, we are taking the "key" and "name" attributes from the item itself
     * For any other optional property we are checking if there is such property in the map
     * *
     * @param model
     * @param options
     */
    function convertToGalleryItemModel(model, options) {
        var map = options.collectionMap;
        var galleryItemModel = new GalleryItemModel();
        var ITEM_COMMON_ATTRIBUTES = ["key","name","backgroundImage","description","isEditable","isViewable","isUserDefault","isSelectable"];

        _.each(ITEM_COMMON_ATTRIBUTES, function(attribute){
            if (_.has(map, attribute) || model.get(attribute)) {
                var attrValue = null;
                if (_.isBoolean(map[attribute])) {
                    attrValue = map[attribute];
                }
                else if (_.isFunction(model[map[attribute]])){
                    attrValue = model[map[attribute]]();
                }
                else {
                    attrValue = getAttributeFromModel(attribute, model, map);
                }

                galleryItemModel.set(attribute, attrValue);
            }
        });

        if (_.isFunction(model.isApiBased)){
            galleryItemModel.set("isApiBased", model.isApiBased());
        }

        if (_.isFunction(model.getTimezone)){
            galleryItemModel.set("timezoneStr", model.getTimezone());
        }

        if (_.has(map, "type")) {
            galleryItemModel.set("type", getAttributeFromModel("type", model, map));
        }

        if (options.ellipsis) {
            galleryItemModel.set("ellipsis", options.ellipsis);
        }
        if (options.descriptionEllipsis) {
            galleryItemModel.set("descriptionEllipsis", options.descriptionEllipsis);
        }

        return galleryItemModel;
    }

    /**
     * Get a model with a collection map (or without) and returns the relevant value
     * @param attrName
     * @param model
     * @param collectionMap
     * @return {*}
     */
    function getAttributeFromModel(attrName, model, collectionMap) {
        var attrValue = model.get(getAttributeName(attrName, collectionMap));
        return attrValue;
    }

    /**
     * Get the relevant attribute name according to the collectionMap
     * @param realAttrName
     * @param collectionMap
     */
    function getAttributeName(realAttrName, collectionMap) {
        var attrName = _.has(collectionMap, realAttrName) ? collectionMap[realAttrName] : realAttrName;
        return attrName;
    }

    Gallery.NEW_ITEM_KEY = NEW_ITEM_KEY;

    Gallery.PERMUTATION = {
        "NONE":"",
        "A":"perm-A"
    };

    Gallery.TRIGGERING = {
        "CHANGE": "change",
        "EDIT": "edit",
        "VIEW": "view",
        "CLICK": "click",
        "DEPENDENCY_CLICK": "dependencyClick"
    };

    Gallery.Collection = GalleryCollection;

    return Gallery;
});
